import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { authService } from '../services/api/auth';
import { useStore, User } from '../store';

// Global auth initialization state
let globalAuthInitialized = false;
let globalAuthPromise: Promise<void> | null = null;

interface UseAuthReturn {
  isAuthChecking: boolean;
  isAuthenticated: boolean;
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  showInactiveAccountDialog: boolean;
  setShowInactiveAccountDialog: (show: boolean) => void;
  isRoleVerified: boolean; // Track jika role sudah di-verify dari database
}

/**
 * Custom hook untuk handle authentication state dan OAuth flow
 * Best practices:
 * - Single source of truth untuk auth state
 * - Proper OAuth callback handling
 * - Role selalu dari database, bukan metadata
 * - Reliable state persistence
 */
export const useAuth = (): UseAuthReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isHydrated, login: storeLogin, logout: storeLogout } = useStore();
  const [isAuthChecking, setIsAuthChecking] = useState(!globalAuthInitialized && !isHydrated);
  const [showInactiveAccountDialog, setShowInactiveAccountDialog] = useState(false);
  const [isRoleVerified, setIsRoleVerified] = useState(false); // Track jika role sudah di-verify dari database

  // Removed restoreBackupState - we always fetch fresh from database to ensure role is correct
  // Zustand persist will handle localStorage restore automatically

  /**
   * Global auth initialization - ensures auth is only initialized once per app
   */
  const initializeGlobalAuth = async (): Promise<void> => {
    if (globalAuthInitialized) return;
    if (globalAuthPromise) return globalAuthPromise;

    globalAuthPromise = (async () => {
      try {
        // Get stored user data as temporary fallback (for faster UI)
        const storedState = useStore.getState();
        const hasStoredUser = storedState.user && storedState.user.role;

        // CRITICAL: Restore authentication state dari localStorage segera
        // Ini mencegah redirect ke login sebelum data profile selesai di-load
        // Kita akan verify dengan database di background, tapi keep UI responsive
        if (hasStoredUser && storedState.isAuthenticated) {
          // Pastikan isAuthenticated true jika ada stored user yang valid
          // Ini memberikan instant UI tanpa menunggu database verification
          useStore.setState({
            isAuthenticated: true,
            user: storedState.user,
          });
        } else if (storedState.isAuthenticated && !hasStoredUser) {
          // Hanya reset jika isAuthenticated true tapi tidak ada user data
          useStore.setState({ isAuthenticated: false });
        }

        // Check if we have a valid session token
        // Reduced timeout dari 2s ke 500ms untuk response lebih cepat
        // Cloudflare bisa menambah latency, jadi timeout pendek lebih baik
        const sessionPromise = supabase.auth.getSession();
        const timeoutPromise = new Promise((resolve) =>
          setTimeout(() => resolve({ timeout: true }), 500),
        );

        const result = (await Promise.race([sessionPromise, timeoutPromise])) as
          | { data: { session: unknown } }
          | { timeout: boolean }
          | { error: unknown };
        let session = (result as { data?: { session?: unknown } })?.data?.session;

        // Quick fallback: try getUser if getSession fails
        if ('timeout' in result || 'error' in result || !session) {
          try {
            // Only call getUser if there's a stored auth token to avoid
            // making unauthenticated requests that return 403 in the network tab.
            const tokenKey = 'supabase.auth.token';
            const storedToken =
              typeof localStorage !== 'undefined' ? localStorage.getItem(tokenKey) : null;
            if (storedToken) {
              const {
                data: { user },
                error: userError,
              } = await supabase.auth.getUser();
              if (user && !userError) {
                session = { user: user as unknown as { id: string } & Record<string, unknown> };
              }
            }
          } catch {
            // Silent fail, continue
          }
        }

        // If session exists, ALWAYS fetch user from database to get fresh role
        // Use stored data only as temporary fallback while fetching
        const typedSession = session as
          | { user?: { id: string } & Record<string, unknown> }
          | undefined;
        if (typedSession?.user) {
          // If we have stored user with same ID, use it temporarily for immediate navigation
          // This provides instant UI feedback while fetching fresh data from DB
          if (
            hasStoredUser &&
            storedState.user.id === typedSession.user.id &&
            storedState.user.role
          ) {
            // Temporarily restore authentication for immediate navigation
            // This prevents showing login page during reload (critical untuk UX)
            useStore.setState({
              user: storedState.user,
              isAuthenticated: true,
            });
          }

          // Fetch fresh user data dari database dengan timeout
          // Cloudflare bisa menambah latency, jadi kita perlu timeout yang reasonable
          try {
            const user = await Promise.race([
              authService.getCurrentUser(typedSession.user),
              new Promise<User | null>(
                (resolve) => setTimeout(() => resolve(null), 8000), // 8s timeout untuk getCurrentUser
              ),
            ]);

            if (user && user.role) {
              // Only login if we have complete user data with role from database
              useStore.getState().login(user);
              // Note: isRoleVerified akan di-set di useEffect setelah state update
            } else {
              // User exists but no role or timeout - clear auth state
              useStore.getState().logout();
            }
          } catch (err) {
            // Jika error (termasuk timeout), gunakan stored data jika masih valid
            // Ini penting untuk mencegah logout saat ada masalah network/Cloudflare
            if (hasStoredUser && storedState.user.id === typedSession.user.id) {
              // Keep using stored data jika fetch gagal (network issue)
              console.warn('Failed to fetch fresh user data, using cached data:', err);
            } else {
              useStore.getState().logout();
            }
          }
        } else {
          // No session - set isAuthenticated to false but keep user data in storage
          if (storedState.isAuthenticated) {
            useStore.setState({ isAuthenticated: false });
          }
        }
      } catch (err: unknown) {
        // Treat 401/403 or explicit unauthorized responses as expected (no session)
        const error = err as {
          status?: number;
          statusCode?: number;
          originalError?: { status?: number };
          message?: string;
        };
        const status =
          error?.status ||
          error?.statusCode ||
          (error?.originalError && error.originalError.status);
        const msg = String(error?.message || '');
        if (
          status === 401 ||
          status === 403 ||
          msg.includes('401') ||
          msg.includes('403') ||
          msg.toLowerCase().includes('unauthorized')
        ) {
          console.debug('Global auth init: unauthenticated (expected).', { status, message: msg });
        } else {
          console.error('Global auth initialization error:', err);
        }
      } finally {
        globalAuthInitialized = true;
      }
    })();

    return globalAuthPromise;
  };

  /**
   * Handle OAuth callback - clean URL hash and query params
   */
  const handleOAuthCallback = useCallback(() => {
    if (
      window.location.hash.includes('access_token') ||
      window.location.hash.includes('code') ||
      window.location.search.includes('code')
    ) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  /**
   * Initialize authentication on mount - global to prevent multiple calls
   */
  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      // If store is already hydrated and we have user data, auth check is complete
      if (isHydrated && user) {
        setIsAuthChecking(false);
        return;
      }

      if (!globalAuthInitialized) {
        await initializeGlobalAuth();
      }

      if (mounted) {
        setIsAuthChecking(false);
      }
    };

    initAuth();

    // No fallback timeout needed - auth processing is now properly managed

    // Listen to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        if (session?.user) {
          // Prevent duplicate login
          const currentUser = useStore.getState().user;
          if (currentUser?.id === session.user.id) {
            setIsAuthChecking(false);
            return;
          }

          try {
            // CRITICAL: Get user from database, not metadata
            // Keep isAuthChecking true until user is successfully fetched
            const user = await authService.getCurrentUser(session.user);
            if (user && mounted) {
              useStore.getState().login(user);
              // CRITICAL: Mark role as verified IMMEDIATELY after DB fetch
              // This prevents ProtectedRoute from checking isProfileCompleted with stale data
              setIsRoleVerified(true);
              handleOAuthCallback();
              // Only set to false after successful login
              setIsAuthChecking(false);
              // Don't redirect here - let the useEffect below handle it after state is updated
            } else if (mounted) {
              // User fetch failed or returned null
              setIsRoleVerified(false);
              setIsAuthChecking(false);
            }
          } catch (error) {
            if (error instanceof Error && error.message.includes('dinonaktifkan')) {
              await supabase.auth.signOut();
              setShowInactiveAccountDialog(true);
            }
            // Set to false even on error to prevent infinite loading
            if (mounted) {
              setIsRoleVerified(false);
              setIsAuthChecking(false);
            }
          }
        } else {
          // No session user, set checking to false
          if (mounted) {
            setIsAuthChecking(false);
          }
        }
      } else if (event === 'SIGNED_OUT') {
        useStore.getState().logout();
        setIsRoleVerified(false);
        if (mounted) {
          setIsAuthChecking(false);
        }
      } else {
        // Other events, set checking to false
        if (mounted) {
          setIsAuthChecking(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [handleOAuthCallback, isHydrated, user]);

  /**
   * Track role verification: set isRoleVerified to true only after user is fetched from database
   * This ensures redirect uses correct role from database, not stale localStorage data
   *
   * Strategy:
   * 1. isRoleVerified is set to true in onAuthStateChange after DB fetch (immediate)
   * 2. For initializeGlobalAuth case, we set it here immediately after auth checking completes
   * 3. This prevents redirect with stale localStorage role data
   */
  useEffect(() => {
    // After auth checking completes and user exists, verify role is from database
    // Set verified immediately if user exists after auth checking (means DB fetch completed)
    if (!isAuthChecking && isAuthenticated && user?.role) {
      // CRITICAL: Set verified immediately - no delay needed
      // If user exists after isAuthChecking completes, it means DB fetch already finished
      // The delay was causing flash to complete-profile page
      const currentState = useStore.getState();
      // Only set verified if user still exists and authenticated (ensures consistency)
      if (currentState.isAuthenticated && currentState.user?.role) {
        setIsRoleVerified(true);
      }
    } else if (!isAuthenticated || !user) {
      setIsRoleVerified(false);
    }
  }, [isAuthChecking, isAuthenticated, user]);

  /**
   * Auto-redirect from public pages based on role
   * CRITICAL: Only redirect when role is CONFIRMED from database
   * Single source of truth for redirect - only redirect when:
   * 1. Auth check is complete (isAuthChecking === false)
   * 2. User is authenticated (CRITICAL: must be true, not just user exists)
   * 3. User exists with valid role (CRITICAL: role must be from database, not storage)
   * 4. Role is verified from database (isRoleVerified === true)
   * 5. Currently on public page
   *
   * IMPORTANT: isAuthenticated must be true (set by storeLogin after DB fetch)
   * AND isRoleVerified must be true (set after successful database fetch)
   * This ensures we only redirect after real database data is available with correct role
   */
  useEffect(() => {
    // Only redirect if ALL conditions are met:
    // - Auth check complete
    // - Authenticated (this is set to true only after DB fetch succeeds)
    // - User exists with role (role is required - no redirect without it)
    // - Role is verified from database (CRITICAL: prevents redirect with stale localStorage role)
    // - On public page
    //
    // This prevents redirect with stale storage data - we wait for:
    // 1. isAuthenticated=true (set after DB fetch)
    // 2. isRoleVerified=true (set after successful DB fetch with role)
    if (
      !isAuthChecking &&
      isAuthenticated &&
      user?.role &&
      isRoleVerified &&
      (location.pathname === '/login' ||
        location.pathname === '/register' ||
        location.pathname === '/')
    ) {
      // CRITICAL: Use role from database (user.role) which is now verified
      const isAdmin = user.role.startsWith('admin') || user.role === 'admin';
      const targetPath = isAdmin ? '/admin' : '/dashboard';
      navigate(targetPath, { replace: true });
    }
  }, [isAuthChecking, isAuthenticated, user, isRoleVerified, location.pathname, navigate]);

  return {
    isAuthChecking,
    isAuthenticated,
    user,
    login: storeLogin,
    logout: storeLogout,
    showInactiveAccountDialog,
    setShowInactiveAccountDialog,
    isRoleVerified,
  };
};
